import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, ChevronDown, ChevronUp, X } from 'lucide-react';

export default function PolicySearch({ policies, officers, onFilteredResults }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [enforceabilityFilter, setEnforceabilityFilter] = useState('all');
  const [designerFilter, setDesignerFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    let filtered = policies;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.title?.toLowerCase().includes(q) ||
        p.statement?.toLowerCase().includes(q) ||
        p.rationale?.toLowerCase().includes(q)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    if (typeFilter !== 'all') {
      if (typeFilter === 'bridging') {
        filtered = filtered.filter(p => p.interim_for_capability_development);
      } else {
        filtered = filtered.filter(p => !p.interim_for_capability_development);
      }
    }

    if (enforceabilityFilter !== 'all') {
      filtered = filtered.filter(p => p.enforceability_score === enforceabilityFilter);
    }

    if (designerFilter !== 'all') {
      filtered = filtered.filter(p => p.designed_by === designerFilter);
    }

    onFilteredResults(filtered);
  }, [searchQuery, statusFilter, typeFilter, enforceabilityFilter, designerFilter, policies]);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setTypeFilter('all');
    setEnforceabilityFilter('all');
    setDesignerFilter('all');
  };

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || typeFilter !== 'all' || enforceabilityFilter !== 'all' || designerFilter !== 'all';

  const designers = [...new Set(policies.map(p => p.designed_by).filter(Boolean))];

  return (
    <Card className="glass-panel border-none mb-4">
      <CardContent className="p-4">
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input data-testid="issues-policysearch-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search policies by title, statement, or rationale..."
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="border-slate-600 text-slate-100 bg-slate-800 hover:bg-slate-700"
            >
              {showFilters ? <ChevronUp className="w-4 h-4 mr-2" /> : <ChevronDown className="w-4 h-4 mr-2" />}
              Filters
            </Button>
            {hasActiveFilters && (
              <Button data-testid="issues-clear-btn" variant="ghost" onClick={clearFilters} className="text-slate-400 hover:text-slate-200">
                <X className="w-4 h-4 mr-2" /> Clear
              </Button>
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-slate-800">
              <div className="space-y-1">
                <label className="text-xs text-slate-400">Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full h-9 rounded-md bg-slate-950 border border-slate-700 text-slate-200 text-sm px-2">
                  <option value="all">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="approved">Approved</option>
                  <option value="institutionalized">Institutionalized</option>
                  <option value="active">Active</option>
                  <option value="under_review">Under Review</option>
                  <option value="retired">Retired</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-slate-400">Type</label>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                  className="w-full h-9 rounded-md bg-slate-950 border border-slate-700 text-slate-200 text-sm px-2">
                  <option value="all">All Types</option>
                  <option value="bridging">Bridging</option>
                  <option value="standing">Standing</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-slate-400">Enforceability</label>
                <select value={enforceabilityFilter} onChange={(e) => setEnforceabilityFilter(e.target.value)}
                  className="w-full h-9 rounded-md bg-slate-950 border border-slate-700 text-slate-200 text-sm px-2">
                  <option value="all">All</option>
                  <option value="easy">Easy</option>
                  <option value="moderate">Moderate</option>
                  <option value="difficult">Difficult</option>
                  <option value="unenforceable">Unenforceable</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-slate-400">Designed By</label>
                <select value={designerFilter} onChange={(e) => setDesignerFilter(e.target.value)}
                  className="w-full h-9 rounded-md bg-slate-950 border border-slate-700 text-slate-200 text-sm px-2">
                  <option value="all">All Officers</option>
                  {designers.map(email => {
                    const o = officers.find(off => off.user_email === email);
                    return <option key={email} value={email}>{o?.callsign || email}</option>;
                  })}
                </select>
              </div>
            </div>
          )}

          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 pt-1">
              {searchQuery && <Badge variant="outline" className="border-indigo-500/30 text-indigo-400">Search: "{searchQuery}"</Badge>}
              {statusFilter !== 'all' && <Badge variant="outline" className="border-emerald-500/30 text-emerald-400">Status: {statusFilter.replace('_', ' ')}</Badge>}
              {typeFilter !== 'all' && <Badge variant="outline" className="border-amber-500/30 text-amber-400">Type: {typeFilter}</Badge>}
              {enforceabilityFilter !== 'all' && <Badge variant="outline" className="border-orange-500/30 text-orange-400">Enforceability: {enforceabilityFilter}</Badge>}
              {designerFilter !== 'all' && (
                <Badge variant="outline" className="border-cyan-500/30 text-cyan-400">
                  Designer: {officers.find(o => o.user_email === designerFilter)?.callsign || designerFilter}
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}